import { Button, Group, Loader, Menu, Table, Text } from '@mantine/core';
import { IconDots, IconDownload, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import dataCaptureClasses from 'src/data-capture/DataCapture.module.css';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { useDeleteDataExport } from 'src/suitability-report/hooks/useDeleteDataExport';
import { useDataExports } from 'src/suitability-report/hooks/useDataExports';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import PageLoader from '../../UI/loader/PageLoader';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import FileUploadLayout from './FileUploadLayout';
import EmptyView from './components/EmptyView';
import FileNameWithIcon from './files-table/FileNameWithIcon';
import { dateMatchesSearch, formatDateTime } from 'src/meetings/utils/date-utils';

export default function DataExports() {
  const { dataExports, isLoading, isError, error, refetch } = useDataExports();
  const { clientId } = useCurrentClient();
  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();
  const {
    deleteDataExport,
    error: deleteDataExportError,
    reset: resetDeleteDataExport,
    isLoading: isDeletingDataExport,
  } = useDeleteDataExport();
  const { flagEnabledDocumentEdit } = useFlags();
  const navigate = useNavigate();

  const openDataExportFlow = () =>
    navigate(`/client/${clientId}/data-export`, { state: { fromPath: location.pathname } });
  const [searchValue, setSearchValue] = useState('');
  const [dataExportToDelete, setDataExportToDelete] = useState<{
    id: string;
    fileName: string;
  } | null>(null);

  const handleDeletePress = (dataExport: { id: string; fileName?: string }) => {
    resetDeleteDataExport();
    setDataExportToDelete({
      id: dataExport.id,
      fileName: dataExport.fileName || 'Untitled',
    });
  };

  const handleDownload = async (dataExportFileId: number) => {
    try {
      const link = await createLink(dataExportFileId);
      window.open(link.url, '_blank');
    } catch (e) {
      notifyAndLogError(
        'Failed to download data export',
        'An error occurred while trying to download the data export. Please try again later.',
        e,
        { clientId, dataExportFileId },
      );
      return;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!dataExportToDelete) return;
    try {
      await deleteDataExport(clientId, dataExportToDelete.id);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete data export',
        'An error occurred while trying to delete the data export. Please try again later.',
        e,
        { clientId, dataExportId: dataExportToDelete.id },
      );
    }
    setDataExportToDelete(null);
    await refetch();
  };

  const filteredDataExports = searchValue.trim()
    ? dataExports.filter(dataExport => {
        const searchLower = searchValue.toLowerCase();
        return (
          (dataExport.fileName || '').toLowerCase().includes(searchLower) ||
          dateMatchesSearch(dataExport.createdAt, searchLower)
        );
      })
    : dataExports;

  const rows = filteredDataExports.map(dataExport => (
    <Table.Tr key={dataExport.id}>
      <Table.Td>
        <FileNameWithIcon
          fileName={dataExport.fileName || 'No file'}
          isFolder={false}
          isGenerated={dataExport.isGenerated}
        />
      </Table.Td>
      <Table.Td>
        <Text size="sm">{dataExport.configDisplayName ?? '\u2014'}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{formatDateTime(dataExport.createdAt)}</Text>
      </Table.Td>
      <Table.Td align="right">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            {flagEnabledDocumentEdit &&
              dataExport.documentHistoryId &&
              (dataExport.backgroundProcess?.status ?? 'SUCCESS') === 'SUCCESS' && (
                <Button
                  variant="primary"
                  onClick={() =>
                    navigate(`/client/${clientId}/document/data-exports/${dataExport.id}/edit`)
                  }
                >
                  Edit
                </Button>
              )}
            {dataExport.fileId ? (
              <Button
                variant={
                  flagEnabledDocumentEdit && dataExport.documentHistoryId ? 'secondary' : 'primary'
                }
                onClick={() => handleDownload(dataExport.fileId!)}
                rightSection={<IconDownload size={16} />}
              >
                Download
              </Button>
            ) : (
              <div />
            )}
          </Group>
          <Menu
            transitionProps={{ transition: 'pop-top-right' }}
            position="bottom-end"
            width={220}
            withinPortal
          >
            <Menu.Target>
              <IconDots color="#475467" stroke={2} size={20} />
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item onClick={() => handleDeletePress(dataExport)}>Delete</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  if (isError || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  if (filteredDataExports.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title="Data exports">
        <EmptyView
          title="Capture essential client information"
          subtitle="Generate comprehensive data export documents with Siesta AI to gather all necessary client details for informed financial planning."
          ctaText="Generate new"
          ctaIcon={IconPlus}
          onCtaClick={openDataExportFlow}
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title="Data exports"
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      actions={
        <Button
          variant="primary"
          leftSection={<IconPlus size={16} />}
          size="md"
          onClick={openDataExportFlow}
        >
          Generate
        </Button>
      }
    >
      <Table.ScrollContainer
        minWidth={600}
        type="native"
        className={dataCaptureClasses.tableScrollContainer}
      >
        <Table
          variant="bare"
          captionSide="bottom"
          classNames={{ th: dataCaptureClasses.th }}
          verticalSpacing={'sm'}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>File name</Table.Th>
              <Table.Th>Configuration</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr>
                <Table.Td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                  <Loader size="sm" />
                </Table.Td>
              </Table.Tr>
            ) : (
              rows
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <ConfirmDeleteModal
        opened={!!dataExportToDelete}
        onClose={() => setDataExportToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete data export"
        message={`Are you sure you want to delete "${dataExportToDelete?.fileName}"? This action cannot be undone.`}
        error={deleteDataExportError}
        isLoading={isDeletingDataExport}
      />
    </FileUploadLayout>
  );
}
